<template>
  <v-container fluid>
    <v-row>
      <v-col class="d-flex flex-column ga-4">
        <v-card
          v-if="draft"
          border
          tile
        >
          <v-card-title>
            {{ $bct('Business information') }}
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-row>
              <LabeledTextfield
                v-model="draft.name"
                @update:model-value="changed = true"
                :hard-lock="isBusinessMember"
                aria-label="Business name"
                description="Your business name will be seen when communicating with support staff. If you have multiple locations, we recommend using your registered business name or LLC."
                message="Business name"
              />
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          border
          tile
        >
          <v-card-title class="d-flex align-center">
            {{ $bct('Locations') }}

            <v-chip
              class="d-flex ms-3 c-black"
              color="grey-lighten-2"
              inline
            >
              {{ providers.length || 0 }}
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-card
              v-for="(provider, index) in providers"
              :key="provider.id"
              :class="{ 'mb-4': index !== providers.length - 1 }"
              border
              tile
            >
              <v-card-text>
                <v-row dense>
                  <v-col class="d-flex align-center">
                    <v-icon
                      class="me-2"
                      color="primary"
                      icon="place"
                    />

                    <div
                      v-text="provider.name"
                      class="fs-16 fw-500"
                    />
                  </v-col>

                  <v-col class="d-flex justify-end align-center">
                    <div
                      v-text="
                        formatAddress(
                          provider?.address1,
                          provider?.city,
                          provider?.state,
                          provider?.zip,
                        )
                      "
                      class="fs-16 fw-500 c-light-black"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>

        <v-card
          v-if="showBankAccount"
          border
          tile
        >
          <v-card-title>
            {{ $bct('Bank account') }}
          </v-card-title>

          <v-divider />

          <v-card-text>
            <p>
              {{
                $bct(
                  'Your banking information is requested in order to deposit funds when you apply for a grant or receive child care payments. This level of permission only allows deposits. We will never withdraw anything from the account.',
                )
              }}
            </p>

            <template v-if="business.bank_account_name">
              <v-divider class="my-3" />

              <div class="d-flex align-center">
                <v-row class="d-flex align-center">
                  <v-col>
                    <div class="fs-14 c-light-black">
                      {{ $bct('Nickname') }}
                    </div>

                    <div
                      v-text="business.bank_account_name || '-'"
                      class="fs-16"
                    />
                  </v-col>

                  <v-col>
                    <div class="fs-14 c-light-black">
                      {{ $bct('Type') }}
                    </div>

                    <div
                      v-text="$bct(business.bank_account_type) || '-'"
                      class="fs-16"
                    />
                  </v-col>

                  <v-col>
                    <div class="fs-14 c-light-black">
                      {{ $bct('Routing number') }}
                    </div>

                    <div
                      v-text="business.bank_routing_number || '-'"
                      class="fs-16"
                    />
                  </v-col>

                  <v-col>
                    <div class="fs-14 c-light-black">
                      {{ $bct('Account number') }}
                    </div>

                    <div
                      v-text="business.bank_account_number_last_four.toString().padStart(10, 'X')"
                      class="fs-16"
                    />
                  </v-col>
                </v-row>
              </div>
            </template>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <StandardButton
              @click="editBankAccount"
              :text="$bct('Update')"
              color="primary"
            />
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <BankAccountDialog
      @save="updateBankAccount"
      ref="bankDialog"
      :processing="processing"
    />

    <BusinessBillingCard
      v-if="business"
      v-model="business"
      :business-api="Api.manager.business"
      :is-editor="true"
    />

    <SaveBar
      v-model="changed"
      @cancel="cancel"
      @save="updateBusiness"
      :processing="processing || undefined"
    />
  </v-container>
</template>

<script setup>
import { useStore } from 'vuex';

import BankAccountDialog from '@/shared/components/billing/BankAccountDialog.vue';
import BusinessBillingCard from '@/shared/components/billing/BusinessBillingCard.vue';
import { formatAddress } from '@/shared/services/address';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import SaveBar from '@/shared/components/form/SaveBar.vue';
import StandardButton from '@/standard/StandardButton.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';

const $bct = inject('translator');

const eventBus = useEventBus();
const store = useStore();
const { api: Api } = useApi();
const { isBusinessMember } = useRole();

const bankDialog = ref(null);
const bankDialogIsVisible = ref(false);
const business = ref(null);
const changed = ref(false);
const draft = ref(null);
const processing = ref(false);
const profile = ref(null);
const providers = ref([]);

const isBankAccountEditable = computed(() => {
  return (
    business.value.billing_contact_first_name &&
    business.value.billing_contact_last_name &&
    business.value.billing_contact_email
  );
});

const showBankAccount = computed(() => {
  if (!business.value || !store.state.pages.Settings.features) return false;

  return (
    store.state.pages.Settings.features.enable_bank_account && store.state.features.enable_payments
  );
});

function cancel() {
  draft.value = JSON.parse(JSON.stringify(business.value || {}));
  changed.value = false;
}

function editBankAccount() {
  if (isBankAccountEditable.value) {
    bankDialog.value.open(business.value);
  } else {
    eventBus.chime(
      'Please ensure billing contact first name, last name, and email are filled in prior to adding a bank account.',
    );
  }
}

async function load() {
  await loadBusiness();
  void loadProfile();
  void loadProviders();
}

async function loadBusiness() {
  const response = await Api.manager.business.getSelf();
  business.value = response.data;
  draft.value = JSON.parse(JSON.stringify(business.value || {}));
}

async function loadProfile() {
  const response = await Api.manager.profile.getSelf();
  profile.value = response.data;
}

async function loadProviders() {
  const response = await Api.manager.provider.index();
  providers.value = response.data;
}

async function updateBankAccount(newValue) {
  processing.value = true;

  const response = await Api.manager.business.update(business.value.id, newValue);

  processing.value = false;
  bankDialogIsVisible.value = false;
  business.value = response.data;
}

async function updateBusiness() {
  const response = await Api.manager.business.update(business.value.id, { name: draft.value.name });
  business.value = response.data;

  changed.value = false;
  eventBus.chime('Saved');
}

onMounted(load);
</script>
